/**
 * WhatsOnChain Blockchain with full adaptive rate limiting (using WhatsOnChainConnector)
 *
 */

import { getLog, script_to_scripthash } from "../helpers/helpers"
import { createSharedWhatsOnChainConnector } from "./WhatsOnChainConnector.js";

const Run = require("runlib")
const BlockchainWrapper = Run.plugins.BlockchainWrapper


// ------------------------------------------------------------------------------------------------
// WhatsOnChainCustom – thin wrapper that implements BlockchainWrapper using the connector
// ------------------------------------------------------------------------------------------------

class WhatsOnChainCustom extends BlockchainWrapper {
    /**
     * @param {?object} options
     * @param {?string} options.apiKey
     * @param {?string} options.network Defaults to 'main'
     * @param {?object} options.logger Optional custom logger
     * @param {?number} options.rateLimitPerSecond Desired rate limit in requests per second (default 3)
     */
    constructor(options = {}) {
        super();

        this.classname = "WhatsOnChainCustom: ";
        const log = getLog(this.classname);
        this.logger = options.logger || log;

        this.api = "whatsonchain"
        this.network = _parseNetwork(options.network);
        this.apiKey = _parseApiKey(options.apiKey);

        // create the app-wide singleton connector — shared between the blockchain
        // (fetch/broadcast/utxos) and the state client (tx:// fallback), so they
        // share the same rate limiter & adaptive throttling.
        this.connector = createSharedWhatsOnChainConnector(
            this.network,
            this.apiKey,
            this.logger,
            options.rateLimitPerSecond ?? 3
        );
    }

    async broadcast(rawtx) {
        return await this.connector.broadcast(rawtx);
    }

    async fetch(txid) {
        try {
            return await this.connector.fetchTxHex(txid);
        } catch (e) {
            if (e.status === 404 || e.status === 500) {
                throw new Error('No such mempool or blockchain transaction');
            }
            throw e;
        }
    }

    async utxos(script) {
        if (this.network === 'stn') {
            if (this.logger._warnOn) this.logger._warn('WhatsOnChainCustom', 'Utxos are not available on STN');
            return [];
        }

        const scripthash = await script_to_scripthash(script);

        const data = await this.connector.fetchUtxosByScripthash(scripthash);

        return data.map(o => ({
            txid: o.tx_hash,
            vout: o.tx_pos,
            satoshis: o.value,
            script
        }));
    }

    async time(txid) {
        try {
            const json = await this.connector.fetchTxInfo(txid);
            return (json.time * 1000) || Date.now();
        } catch (e) {
            if (e.status === 404 || e.status === 500) {
                throw new Error('No such mempool or blockchain transaction');
            }
            throw e;
        }
    }

    async spends(txid, vout) {
        throw new Error('WhatsOnChain API does not support spends');
    }
}

// ------------------------------------------------------------------------------------------------
// Parameter validation
// ------------------------------------------------------------------------------------------------

function _parseApiKey(apiKey) {
    if (typeof apiKey === 'undefined' || typeof apiKey === 'string') return apiKey ? apiKey.trim() : null;
    throw new Error(`Invalid API key: ${apiKey}`);
}

function _parseNetwork(network) {
    if (typeof network === 'undefined') return 'main';
    if (typeof network !== 'string') throw new Error(`Invalid network: ${network}`);
    if (!['main', 'test', 'stn'].includes(network)) {
        throw new Error(`WhatsOnChain API does not support the "${network}" network`);
    }
    return network;
}

// ------------------------------------------------------------------------------------------------

export default WhatsOnChainCustom;
